import React, { useEffect, useRef, useState } from 'react'
import { Button, Card, Collapse, Input, Select, Typography } from 'antd'
import { KeyOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '~/Store'
import {
  getServicePrincipals,
  ServicePrincipalActions,
} from '~/Store/ServicePrincipal'
import {
  ApiKeyActions,
  getApiKeyUpdated,
  getIsLoadingClaims,
  getIsLoadingProducts,
  getIsUpdatingApiKey,
  getProductClaimsMap,
  getProducts,
  getViewClaims,
} from '~/Store/ApiKey'
import { ApiKeyStatus, IApiKey } from '~/Services/ApiKey.interface'
import { ProductHeader } from './ProductHeader'
import { ProductPermissions } from './ProductPermissions'
import {
  ExpirySelector,
  ExpirySelection,
  initialExpirySelection,
  resolveExpiresAt,
} from './ExpirySelector'

const { Panel } = Collapse
const { Option } = Select

export const EditApiKey = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation<{ apiKey?: IApiKey } | undefined>()
  const apiKey = location.state?.apiKey
  const dispatch = useAppDispatch()

  const servicePrincipals = useAppSelector(getServicePrincipals)
  const products = useAppSelector(getProducts)
  const productClaimsMap = useAppSelector(getProductClaimsMap)
  const isLoadingProducts = useAppSelector(getIsLoadingProducts)
  const viewClaims = useAppSelector(getViewClaims)
  const isLoadingClaims = useAppSelector(getIsLoadingClaims)
  const isUpdating = useAppSelector(getIsUpdatingApiKey)
  const apiKeyUpdated = useAppSelector(getApiKeyUpdated)

  const [name, setName] = useState(apiKey?.name ?? '')
  const [status, setStatus] = useState<ApiKeyStatus | undefined>(apiKey?.status)
  const [expirySelection, setExpirySelection] = useState<ExpirySelection>(
    initialExpirySelection(apiKey?.expiresAt),
  )
  const [expandedProductIds, setExpandedProductIds] = useState<string[]>([])
  const [selectedClaimIds, setSelectedClaimIds] = useState<
    Record<string, string[]>
  >({})
  const claimsInitialized = useRef(false)

  useEffect(() => {
    if (!apiKey) {
      history.replace('/Organizations/ApiKeys')
      return
    }
    dispatch(ServicePrincipalActions.getServicePrincipals({ paging: false }))
    dispatch(ApiKeyActions.getProducts())
    dispatch(ApiKeyActions.getApiKeyClaims(apiKey.id))
    return () => {
      dispatch(ApiKeyActions.resetViewClaims())
    }
  }, [])

  useEffect(() => {
    if (apiKeyUpdated) {
      dispatch(ApiKeyActions.resetApiKeyUpdated())
      history.goBack()
    }
  }, [apiKeyUpdated])

  useEffect(() => {
    if (!apiKey) return
    products.forEach(product => {
      dispatch(
        ApiKeyActions.getProductClaims({
          servicePrincipalId: apiKey.servicePrincipalId,
          productId: product.guid,
          audience: product.clientId,
        }),
      )
    })
  }, [apiKey, products])

  // The key's current claims (GET .../claims) come back as a flat list with
  // no productId — cross-reference each productClaimId against the SP's
  // already-fetched per-product claim lists to know which panel it belongs
  // to, then seed the selection once (a ref guards against re-running and
  // clobbering the user's own edits on a later re-render).
  useEffect(() => {
    if (claimsInitialized.current || !apiKey || !viewClaims) return
    if (products.length === 0) return
    const allLoaded = products.every(
      p =>
        productClaimsMap[`${apiKey.servicePrincipalId}:${p.guid}`] !==
        undefined,
    )
    if (!allLoaded) return

    const assignedIds = new Set(viewClaims.claims.map(c => c.productClaimId))
    const grouped: Record<string, string[]> = {}
    products.forEach(product => {
      const claims =
        productClaimsMap[`${apiKey.servicePrincipalId}:${product.guid}`] || []
      const matched = claims
        .filter(c => assignedIds.has(c.productClaimId))
        .map(c => c.productClaimId)
      if (matched.length) grouped[product.guid] = matched
    })
    setSelectedClaimIds(grouped)
    setExpandedProductIds(Object.keys(grouped))
    claimsInitialized.current = true
  }, [apiKey, viewClaims, products, productClaimsMap])

  if (!apiKey) return null

  const servicePrincipalName =
    servicePrincipals.find(sp => sp.id === apiKey.servicePrincipalId)?.name ??
    apiKey.servicePrincipalId

  const totalServicesSelected = Object.values(selectedClaimIds).filter(
    ids => ids.length > 0,
  ).length

  const toggleProduct = (
    productId: string,
    checked: boolean,
    allClaimIds: string[],
  ) => {
    setSelectedClaimIds(prev => ({
      ...prev,
      [productId]: checked ? allClaimIds : [],
    }))
    if (checked && !expandedProductIds.includes(productId)) {
      setExpandedProductIds(prev => [...prev, productId])
    }
  }

  const toggleClaim = (productId: string, claimId: string, checked: boolean) => {
    setSelectedClaimIds(prev => {
      const current = prev[productId] || []
      return {
        ...prev,
        [productId]: checked
          ? [...current, claimId]
          : current.filter(id => id !== claimId),
      }
    })
  }

  const deselectAll = (productId: string) => {
    setSelectedClaimIds(prev => ({ ...prev, [productId]: [] }))
  }

  const cancelClick = () => {
    history.goBack()
  }

  const saveClick = () => {
    if (!name) return
    const expiresAt = resolveExpiresAt(expirySelection)
    const productClaimIds = Object.values(selectedClaimIds).flat()
    dispatch(
      ApiKeyActions.updateApiKey({
        apiKeyId: apiKey.id,
        servicePrincipalId: apiKey.servicePrincipalId,
        data: { name, status, expiresAt },
        productClaimIds,
      }),
    )
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '20px',
        }}
      >
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            <KeyOutlined style={{ marginRight: '8px' }} />
            {t('admin.akEditApiKey')}
          </Typography.Title>
          <Typography.Text type="secondary">
            {t('admin.akDescription')}
          </Typography.Text>
        </div>
        <div>
          <Button onClick={cancelClick} style={{ marginRight: '10px' }}>
            {t('cancel')}
          </Button>
          <Button
            type="primary"
            loading={isUpdating}
            disabled={!name}
            onClick={saveClick}
          >
            {t('save')}
          </Button>
        </div>
      </div>

      <Card title={t('admin.akKeyDetails')} style={{ marginBottom: '20px' }}>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '6px' }}>{t('admin.akServicePrincipal')}</div>
          <Input readOnly disabled value={servicePrincipalName} />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '6px' }}>
            {t('admin.name')} <span style={{ color: 'red' }}>*</span>
          </div>
          <Input value={name} onChange={evt => setName(evt.target.value)} />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '6px' }}>{t('admin.status')}</div>
          <Select style={{ width: '100%' }} value={status} onChange={setStatus}>
            <Option value={ApiKeyStatus.Active}>{t('admin.akStatusActive')}</Option>
            <Option value={ApiKeyStatus.Revoked}>{t('admin.akStatusRevoked')}</Option>
            <Option value={ApiKeyStatus.Expired}>{t('admin.akStatusExpired')}</Option>
            <Option value={ApiKeyStatus.Inactive}>{t('admin.akStatusInactive')}</Option>
          </Select>
        </div>

        <div>
          <div style={{ marginBottom: '6px' }}>{t('admin.akExpiry')}</div>
          <ExpirySelector value={expirySelection} onChange={setExpirySelection} />
        </div>
      </Card>

      <Card
        title={t('admin.akServiceProviders')}
        extra={
          <span>
            {t('admin.akSelectedCount', { count: totalServicesSelected })}
          </span>
        }
        loading={isLoadingProducts || isLoadingClaims}
      >
        <Collapse
          activeKey={expandedProductIds}
          onChange={keys => setExpandedProductIds(keys as string[])}
        >
          {products.map(product => (
            <Panel
              key={product.guid}
              header={
                <ProductHeader
                  product={product}
                  servicePrincipalId={apiKey.servicePrincipalId}
                  selectedClaimIds={selectedClaimIds[product.guid] || []}
                  onToggleProduct={(checked, allClaimIds) =>
                    toggleProduct(product.guid, checked, allClaimIds)
                  }
                />
              }
            >
              <ProductPermissions
                productId={product.guid}
                servicePrincipalId={apiKey.servicePrincipalId}
                selectedClaimIds={selectedClaimIds[product.guid] || []}
                onToggleClaim={(claimId, checked) =>
                  toggleClaim(product.guid, claimId, checked)
                }
                onDeselectAll={() => deselectAll(product.guid)}
              />
            </Panel>
          ))}
        </Collapse>
      </Card>
    </div>
  )
}
